// src/components/admin/scheduling/components/ScheduleActivityToast.js
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./ScheduleActivityToast.css";
import { collection, onSnapshot, orderBy, limit, query, Timestamp } from "firebase/firestore";
import { db } from "../../../../firebaseConfig";
import { FiInfo, FiX } from "react-icons/fi";

// ─── Change this constant to adjust auto-dismiss timing ───────────────────────
export const SCHEDULE_ALERT_DURATION_MS = 300000; // 5 minutes
// ─────────────────────────────────────────────────────────────────────────────

const LS_KEY = "schedToastPersisted";

function saveToStorage(toasts) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(toasts));
  } catch {
    // no-op — storage unavailable
  }
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return parsed.filter((t) => t.expiresAt && t.expiresAt > now);
  } catch {
    return [];
  }
}

function getAlertTitle(actionType) {
  switch ((actionType || "").toUpperCase()) {
    case "CREATE_SESSION":   return "Session Created";
    case "MOVE_SESSION":     return "Session Moved";
    case "STATUS_CHANGE":    return "Status Updated";
    case "TUTOR_CHANGE":     return "Tutor Changed";
    case "SUBJECT_CHANGE":   return "Subject Changed";
    case "NOTE_CHANGE":      return "Note Updated";
    case "DELETE_SESSION":   return "Session Deleted";
    case "RESTORE_SESSION":  return "Session Restored";
    case "MULTI_UPDATE":     return "Session Updated";
    case "BATCH_DELETE":     return "Sessions Deleted";
    case "BATCH_UPDATE":     return "Batch Update";
    // ── Events ──
    case "CREATE_EVENT":     return "Event Created";
    case "MOVE_EVENT":       return "Event Moved";
    case "UPDATE_EVENT":     return "Event Updated";
    case "DELETE_EVENT":     return "Event Deleted";
    default:                 return "Schedule Update";
  }
}

function buildAlertSentence(log) {
  const m = log.meta || {};
  const student = m.studentName || "a student";
  const tutor   = m.tutorName   || "";
  const context = m.sessionContext || "";
  const from    = (m.valFrom || "").trim();
  const to      = (m.valTo   || "").trim();
  const type    = (log.actionType || "").toUpperCase();
  const ctx     = context ? ` — ${context}` : "";

  switch (type) {
    case "CREATE_SESSION":
      return `Session created for ${student}${tutor ? ` with ${tutor}` : ""}${ctx}`;
    case "MOVE_SESSION":
      return `Session moved for ${student}${from && to ? ` from ${from} to ${to}` : ""}${ctx}`;
    case "STATUS_CHANGE":
      return `Status changed for ${student}${from && to ? ` from ${from} to ${to}` : ""}${ctx}`;
    case "TUTOR_CHANGE":
      return `Tutor changed for ${student}${from && to ? ` from ${from} to ${to}` : ""}${ctx}`;
    case "SUBJECT_CHANGE":
      return `Subject changed for ${student}${from && to ? ` from ${from} to ${to}` : ""}${ctx}`;
    case "NOTE_CHANGE":
      return `Note updated for ${student}'s session${ctx}`;
    case "DELETE_SESSION":
      return `Session deleted for ${student}${ctx}`;
    case "RESTORE_SESSION":
      return `Session restored for ${student}${ctx}`;
    case "BATCH_DELETE": {
      const lines = context.split("\n").map((l) => l.trim()).filter(Boolean);
      const preview = lines[0] || "";
      const extra = lines.length > 1 ? ` +${lines.length - 1} more` : "";
      return `${lines.length || 0} session${lines.length === 1 ? "" : "s"} deleted${preview ? ` — ${preview}${extra}` : ""}`;
    }
    // ── Events ──
    case "CREATE_EVENT":
      return `Event created: ${student}${ctx}`;
    case "MOVE_EVENT":
      return `Event moved: ${student}${from && to ? ` — ${from} → ${to}` : ""}`;
    case "UPDATE_EVENT":
      return `Event updated: ${student}${ctx}`;
    case "DELETE_EVENT":
      return `Event deleted: ${student}${ctx}`;
    case "MULTI_UPDATE": {
      const labels = from
        .split("\n")
        .map((line) => { const ci = line.indexOf(":"); return ci > -1 ? line.substring(0, ci).trim().toLowerCase() : null; })
        .filter(Boolean);
      return `Multiple fields updated for ${student}${labels.length ? `: ${labels.join(", ")}` : ""}${ctx}`;
    }
    default:
      return `${(type || "action").toLowerCase().replace(/_/g, " ")} for ${student}${ctx}`;
  }
}

export default function ScheduleActivityToast() {
  // Load persisted toasts (from previous page visit, still within 5-min window)
  const [toasts, setToasts] = useState(() => loadFromStorage());
  const timerRef = useRef(new Map());
  // Only surface logs written after this component first mounted in this session.
  const mountTsRef = useRef(null);

  // On mount: start expiry timers for any persisted toasts that survived refresh
  useEffect(() => {
    mountTsRef.current = Timestamp.now();
    const now = Date.now();

    setToasts((prev) => {
      const valid = prev.filter((t) => t.expiresAt && t.expiresAt > now);
      // Kick off countdown timers for persisted toasts
      for (const t of valid) {
        if (!timerRef.current.has(t.id)) {
          const remaining = t.expiresAt - now;
          const timer = window.setTimeout(() => {
            setToasts((p) => {
              const next = p.filter((x) => x.id !== t.id);
              saveToStorage(next);
              return next;
            });
            timerRef.current.delete(t.id);
          }, remaining);
          timerRef.current.set(t.id, timer);
        }
      }
      return valid;
    });

    const q = query(
      collection(db, "activity_logs"),
      orderBy("timestamp", "desc"),
      limit(5)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const mountTs = mountTsRef.current;
        if (!mountTs) return;

        const added = snap.docChanges()
          .filter((c) => {
            if (c.type !== "added") return false;
            const ts = c.doc.data().timestamp;
            if (!ts || typeof ts.toMillis !== "function") return false;
            return ts.toMillis() > mountTs.toMillis();
          })
          .map((c) => ({ id: c.doc.id, ...c.doc.data() }));

        if (!added.length) return;

        const expiresAt = Date.now() + SCHEDULE_ALERT_DURATION_MS;

        setToasts((prev) => {
          const next = [...prev];
          for (const entry of added) {
            if (next.some((t) => t.id === entry.id)) continue;
            next.unshift({ ...entry, expiresAt });
          }
          const sliced = next.slice(0, 3);
          saveToStorage(sliced);
          return sliced;
        });

        for (const entry of added) {
          const existing = timerRef.current.get(entry.id);
          if (existing) window.clearTimeout(existing);

          const timer = window.setTimeout(() => {
            setToasts((prev) => {
              const next = prev.filter((t) => t.id !== entry.id);
              saveToStorage(next);
              return next;
            });
            timerRef.current.delete(entry.id);
          }, SCHEDULE_ALERT_DURATION_MS);

          timerRef.current.set(entry.id, timer);
        }
      },
      (err) => {
        console.error("[ScheduleActivityToast] Firestore listener error:", err);
      }
    );

    const timers = timerRef.current;
    return () => {
      unsub();
      timers.forEach((t) => window.clearTimeout(t));
      timers.clear();
    };
  }, []);

  if (!toasts.length) return null;

  return ReactDOM.createPortal(
    <div className="schedToastContainer" aria-live="polite" aria-label="Schedule activity notifications">
      {toasts.map((toast) => (
        <div key={toast.id} className="schedToastAlert">
          <div className="schedToastIcon">
            <FiInfo size={15} />
          </div>
          <div className="schedToastContent">
            <div className="schedToastTitle">{getAlertTitle(toast.actionType)}</div>
            <div className="schedToastDesc">{buildAlertSentence(toast)}</div>
          </div>
          <button
            type="button"
            className="schedToastClose"
            aria-label="Dismiss"
            onClick={() => {
              const timer = timerRef.current.get(toast.id);
              if (timer) {
                window.clearTimeout(timer);
                timerRef.current.delete(toast.id);
              }
              setToasts((prev) => {
                const next = prev.filter((t) => t.id !== toast.id);
                saveToStorage(next);
                return next;
              });
            }}
          >
            <FiX size={13} />
          </button>
        </div>
      ))}
    </div>,
    document.body
  );
}
